// src/components/billing/UsageChart.tsx
/**
 * Chart component for displaying usage history over time.
 * Uses ECharts for rendering.
 */

import React from "react";
import { Box, useTheme } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { UsageHistoryItem } from "../../hooks/useBilling";
import { glass } from "../../theme/glassTokens";

interface UsageChartProps {
  data: UsageHistoryItem[];
  metric?: "cost" | "tokens" | "documents" | "voice_minutes" | "extractions";
  height?: number;
}

const UsageChart: React.FC<UsageChartProps> = ({
  data,
  metric = "tokens",
  height = 250,
}) => {
  const theme = useTheme();

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const isCostMetric = metric === "cost";

  // Get label for metric
  const getMetricLabel = () => {
    switch (metric) {
      case "cost":
        return "Cost (€)";
      case "tokens":
        return "Tokens";
      case "documents":
        return "Documents";
      case "voice_minutes":
        return "Voice Minutes";
      case "extractions":
        return "Extractions";
      default:
        return "Usage";
    }
  };

  // Get color for metric
  const getMetricColor = () => {
    switch (metric) {
      case "cost":
        return theme.palette.success.light;
      case "tokens":
        return glass.accent;
      case "documents":
        return theme.palette.secondary.main;
      case "voice_minutes":
        return theme.palette.success.light;
      case "extractions":
        return glass.accent;
      default:
        return glass.accent;
    }
  };

  // Extract the chart values — cost needs cents→dollars conversion
  const chartValues = data.map((d) => {
    if (isCostMetric) return d.cost_cents / 100;
    return d[metric as keyof Omit<UsageHistoryItem, "date" | "cost_cents">] as number;
  });

  const chartOption = {
    tooltip: {
      trigger: "axis",
      backgroundColor: glass.tableHeader,
      borderColor: glass.panelBorder,
      textStyle: {
        color: glass.textPrimary,
      },
      formatter: (params: any) => {
        const dataPoint = params[0];
        const formattedValue = isCostMetric
          ? `€${dataPoint.value.toFixed(2)}`
          : dataPoint.value.toLocaleString();
        return `
          <div style="font-weight: 500;">${dataPoint.name}</div>
          <div style="margin-top: 4px;">
            <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${dataPoint.color}; margin-right: 8px;"></span>
            ${getMetricLabel()}: <strong>${formattedValue}</strong>
          </div>
        `;
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "3%",
      top: "10%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: data.map((d) => formatDate(d.date)),
      axisLine: {
        lineStyle: {
          color: glass.rowDivider,
        },
      },
      axisLabel: {
        color: glass.textSecondary,
        fontSize: 11,
        rotate: data.length > 15 ? 45 : 0,
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      type: "value",
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: glass.textSecondary,
        fontSize: 11,
        formatter: (value: number) => {
          if (isCostMetric) {
            if (value >= 1000) return `€${(value / 1000).toFixed(1)}K`;
            return `€${value.toFixed(value < 1 ? 2 : 0)}`;
          }
          if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
          return value.toString();
        },
      },
      splitLine: {
        lineStyle: {
          color: glass.rowDivider,
          type: "dashed",
        },
      },
    },
    series: [
      {
        name: getMetricLabel(),
        type: "bar",
        data: chartValues,
        itemStyle: {
          color: getMetricColor(),
          borderRadius: [4, 4, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: isCostMetric
              ? theme.palette.success.light
              : glass.accent,
          },
        },
        barMaxWidth: 40,
      },
    ],
  };

  return (
    <Box sx={{ width: "100%", height }}>
      <ReactECharts
        option={chartOption}
        style={{ width: "100%", height: "100%" }}
        opts={{ renderer: "canvas" }}
      />
    </Box>
  );
};

export default UsageChart;
